import { Button, Field, Input, Modal, Textarea } from '@quickreport/ui';
import { Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, describeError } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';

interface Req {
  canRequest: boolean;
  request: {
    id: string;
    status: 'pending' | 'approved' | 'declined' | 'cancelled';
    reason: string;
    requestedByName: string;
    createdAt: string;
    decidedAt: string | null;
    decisionNote: string | null;
  } | null;
}

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Ask LabView to delete this lab. Nothing is deleted here: the request goes to the people who run
 * LabView, who approve or decline it. Only an owner of the lab can ask.
 */
export function DeleteLabPanel() {
  const ws = useWorkspace();
  const [data, setData] = useState<Req | null>(null);
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<Req>('GET', '/v1/org/deletion-request'));
    } catch {
      // Offline: the section just stays hidden until the next visit.
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  if (!data?.canRequest) return null;
  const r = data.request;
  const pending = r?.status === 'pending';
  const labName = ws.auth.orgName.trim();
  const matches = typed.trim() === labName;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/v1/org/deletion-request', { confirmName: typed, reason });
      setAsking(false);
      setTyped('');
      setReason('');
      await load();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {
      await api('POST', '/v1/org/deletion-request/cancel');
      await load();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="grid gap-3 rounded-lg border border-flag/30 p-4">
      <h2 className="text-base font-semibold text-flag">Delete this lab</h2>
      {pending && r ? (
        <>
          <p className="text-[13px]">
            You asked for this lab to be deleted on {day(r.createdAt)}. It is waiting for LabView to
            approve it. Until then nothing changes and everyone can keep working.
          </p>
          {error ? (
            <p role="alert" className="text-[13px] text-flag">
              {error}
            </p>
          ) : null}
          <div>
            <Button size="sm" disabled={busy} onClick={() => void cancel()}>
              Cancel the request
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-[13px] text-slate">
            Ask LabView to delete this lab and everything in it, so you can start again with a fresh
            lab. LabView reviews every request, and nothing is deleted until it is approved.
          </p>
          {r?.status === 'declined' ? (
            <p className="rounded-md bg-canvas px-3 py-2 text-[13px]">
              Your request of {day(r.createdAt)} was not approved
              {r.decisionNote ? `: “${r.decisionNote}”` : '.'}
            </p>
          ) : null}
          <div>
            <Button tone="danger" size="sm" onClick={() => setAsking(true)}>
              <Trash2 size={13} /> Ask to delete this lab…
            </Button>
          </div>
        </>
      )}

      {asking ? (
        <Modal
          title="Ask to delete this lab?"
          label="Ask to delete this lab"
          onClose={() => setAsking(false)}
        >
          <div className="grid gap-3 text-[13px]">
            <p>
              If LabView approves, <b>{labName}</b> is deleted for good: every report, patient,
              template, payment, setting, logo and signature. Patients’ report links stop working.
            </p>
            <p className="text-slate">
              Everyone in the lab is signed out. You keep your email or mobile sign-in, and next
              time you sign in you can set up a new lab. Download anything you need first.
            </p>
            <Field label="Why do you want to delete it? (optional)">
              <Textarea
                rows={2}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <Field label={`Type the lab’s name to confirm: ${labName}`}>
              <Input
                value={typed}
                autoComplete="off"
                onChange={(e) => setTyped(e.target.value)}
                aria-label="Type the lab name to confirm"
              />
            </Field>
            {error ? (
              <p role="alert" className="text-flag">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button onClick={() => setAsking(false)} disabled={busy}>
                Keep my lab
              </Button>
              <Button
                tone="danger"
                className="border border-flag"
                disabled={!matches || busy}
                onClick={() => void send()}
              >
                {busy ? 'Sending…' : 'Send the request'}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
